/**
 * ContratoTemplate — cadastro comercial + ações "Provisionar" (Contrato.provisionar) e "Trocar
 * Modalidade" (Contrato.trocarModalidade).
 *
 * Alinhado a hay-hotspot/assets/reactweb/src/pages/Auth/Contrato/ContratoTemplate.js.
 */
import React, { Component } from 'react';
import { TabPanel } from 'primereact/tabview';
import { confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';

import { TabViewComErro } from 'hay-core-react';
import { EntityGrid } from 'hay-core-react';
import { ModelTextInput } from 'hay-core-react';
import { ModelTextArea } from 'hay-core-react';
import { ModelDropDown } from 'hay-core-react';
import { ModelCalendar } from 'hay-core-react';
import { UtilService } from 'hay-core-react';
import Contrato from '../../../models/Contrato';
import Modalidade from '../../../models/Modalidade';
import { Publicacao } from 'hay-core-react';
import ContratoSituacaoEnum from '../../../enums/ContratoSituacaoEnum';
import ContratoUfEnum from '../../../enums/ContratoUfEnum';

const model = {
  razaosocial: { exibir: 'main.contrato.razaosocial', visivel: true, validacao: { obrigatorio: true } },
  nomefantasia: { exibir: 'main.contrato.nomefantasia' },
  cnpj: { exibir: 'main.contrato.cnpj', visivel: true, validacao: { obrigatorio: true } },
  email: { exibir: 'main.contrato.email', keyfilter: 'email', validacao: { obrigatorio: true, tamanhominimo: 3, email: true } },
  telefone: { exibir: 'main.contrato.telefone', keyfilter: 'num', validacao: { obrigatorio: true, tamanhominimo: 8 } },
  cep: { exibir: 'main.contrato.cep', validacao: { obrigatorio: true } },
  logradouro: { exibir: 'main.contrato.logradouro', validacao: { obrigatorio: true } },
  numero: { exibir: 'main.contrato.numero', validacao: { obrigatorio: true } },
  complemento: { exibir: 'main.contrato.complemento' },
  bairro: { exibir: 'main.contrato.bairro', validacao: { obrigatorio: true } },
  cidade: { exibir: 'main.contrato.cidade', validacao: { obrigatorio: true } },
  uf: { exibir: 'main.contrato.uf', tipo: 'enum', entidade: 'contrato.uf', validacao: { obrigatorio: true } },
  modalidade: {
    exibir: 'main.contrato.modalidade',
    visivel: true,
    tipo: 'relacao',
    camporelacao: 'nome',
    entidade: 'modalidade.entidade',
    template: 'modalidade.template',
    validacao: { obrigatorio: true },
  },
  situacao: { exibir: 'main.contrato.situacao', visivel: true, tipo: 'enum', entidade: 'contrato.situacao' },
  responsavel: { exibir: 'main.contrato.responsavel', validacao: { obrigatorio: true, tamanhominimo: 3 } },
  datacontratacao: { exibir: 'main.contrato.datacontratacao', tipo: 'date', time: true },
  observacoes: { exibir: 'main.contrato.observacoes' },
  cnamedesejado: { exibir: 'main.contrato.cnamedesejado', validacao: { obrigatorio: true } },
  publicacaocontrato: {
    exibir: 'main.contrato.publicacaocontrato',
    visivel: true,
    tipo: 'relacao',
    camporelacao: 'descricao',
    entidade: 'publicacaocontrato.entidade',
    template: 'publicacaocontrato.template',
  },
};

const renderers = {
  'modalidade.entidade': Modalidade,
  'modalidade.template': (rowData) => rowData.modalidade?.nome || '',
  'contrato.uf': ContratoUfEnum,
  'contrato.situacao': ContratoSituacaoEnum,
  'publicacaocontrato.entidade': Publicacao,
  'publicacaocontrato.template': (rowData) => rowData.publicacaocontrato?.descricao || '',
};

class ContratoFormulario extends Component {
  constructor(props) {
    super(props);
    this.state = { consultando: false };
  }

  // Consulta a Receita Federal (via Contrato.consultarCnpj) e pré-preenche os campos
  // retornados, um a um via contexto.updateModelPath - nunca via UtilService.prepararModel
  // (que sobrescreveria TODOS os campos do schema, inclusive os não retornados, com
  // undefined). Não passa pelo lifecycle do model - só é persistido quando "Salvar" é
  // pressionado no EntityGrid.
  consultarCnpj = async () => {
    const { model, contexto } = this.props;
    const cnpj = (model.cnpj.value || '').replace(/\D/g, '');
    if (cnpj.length !== 14) {
      UtilService.showToast({ severity: 'warn', summary: UtilService.traduzir('main.contrato.consultarcnpj.invalido') });
      return;
    }

    this.setState({ consultando: true });
    const resultado = await Contrato.consultarCnpj(cnpj);
    this.setState({ consultando: false });

    if (!resultado.status) {
      const detalhe = UtilService.traduzir(resultado?.data?.state) || resultado?.data?.mensagem || '';
      UtilService.showToast({ sticky: true, severity: 'error', summary: UtilService.traduzir('main.contrato.consultarcnpj.erro'), detail: detalhe });
      return;
    }

    // uf e tipo:'enum' - ModelDropDown (modo nao-servidor) espera model.value como objeto
    // {id, descricao} (ver ModelDropDown.valueTemplate/render, compara com as opcoes via
    // dataKey="id"), nao a string crua - sem isso o dropdown fica em branco mesmo com o
    // valor certo já setado.
    Object.entries(resultado.data || {}).forEach(([campo, valor]) => {
      const valorModel = campo === 'uf' ? { id: valor, descricao: valor } : valor;
      contexto.updateModelPath(campo + '.value', valorModel);
    });
    UtilService.showToast({ severity: 'success', summary: UtilService.traduzir('main.contrato.consultarcnpj.sucesso') });
  }

  render() {
    const { model, contexto } = this.props;
    return (
      <TabViewComErro tentativaSalvar={contexto.state.tentativaSalvar} className="col-12 mt-2">
        <TabPanel header={UtilService.traduzir('main.contrato.aba.dadosgerais')} leftIcon="pi pi-building mr-2">
          <div className="grid">
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.cnpj} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3 flex align-items-center">
              <Button
                type="button"
                label={UtilService.traduzir('main.contrato.consultarcnpj')}
                icon="pi pi-search"
                className="p-button-outlined w-full"
                loading={this.state.consultando}
                onClick={this.consultarCnpj}
              />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.razaosocial} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.nomefantasia} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.email} contexto={contexto} onBlur={() => contexto.updateModelPath('email.value', (model.email.value || '').toLowerCase().trim())} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.telefone} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-5 mt-3">
              {/* Somente-leitura fora da criação - depois que o contrato existe, a modalidade só
                  pode mudar via processo "Trocar Modalidade" (fecha/reabre o período de
                  ContratoUtilizacao vigente); editar aqui direto puxaria o grid padrão, pulando
                  esse fechamento e corrompendo o histórico de faturamento. */}
              <ModelDropDown model={{ ...model.modalidade, disabled: contexto.state.formulario !== 'novo' }} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-3 mt-3">
              <ModelDropDown model={model.situacao} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-4 mt-3">
              <ModelCalendar model={model.datacontratacao} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.responsavel} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.cnamedesejado} contexto={contexto} />
            </div>
            <div className="field col-12 mt-3">
              <ModelTextArea model={model.observacoes} contexto={contexto} />
            </div>
          </div>
        </TabPanel>
        <TabPanel header={UtilService.traduzir('main.contrato.aba.endereco')} leftIcon="pi pi-map-marker mr-2">
          <div className="grid">
            <div className="field col-12 md:col-3 mt-3">
              <ModelTextInput model={model.cep} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-6 mt-3">
              <ModelTextInput model={model.logradouro} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-3 mt-3">
              <ModelTextInput model={model.numero} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-12 mt-3">
              <ModelTextInput model={model.complemento} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-4 mt-3">
              <ModelTextInput model={model.bairro} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-5 mt-3">
              <ModelTextInput model={model.cidade} contexto={contexto} />
            </div>
            <div className="field col-12 md:col-3 mt-3">
              <ModelDropDown model={model.uf} contexto={contexto} />
            </div>
          </div>
        </TabPanel>
      </TabViewComErro>
    );
  }
}

class ContratoTemplate extends Component {

  constructor(props) {
    super(props);
    this.state = {
      trocarModalidadeVisivel: false,
      trocarModalidadeOpcoes: [],
      trocarModalidadeSelecionada: null,
      trocarModalidadeSalvando: false,
    };
  }

  // Processo "Trocar Modalidade": fecha o período de ContratoUtilizacao vigente (excedente
  // calculado com o uso acumulado até agora) e abre um novo já sob a modalidade escolhida (ver
  // Contrato.trocarModalidade). Diferente de "Provisionar" (só confirma), precisa de um campo a
  // mais (a nova modalidade) - por isso usa um Dialog próprio com Dropdown em vez do
  // confirmDialog simples.
  abrirTrocarModalidade = async () => {
    const contrato = this.gridRef?.getSelected()?.[0];
    if (!contrato) return;

    const resultado = await Modalidade.list({ ativo: true }, null, { rows: 100, first: 0 });
    const opcoes = resultado?.data?.registros || [];
    this.setState({
      trocarModalidadeVisivel: true,
      trocarModalidadeOpcoes: opcoes,
      trocarModalidadeSelecionada: opcoes.find((m) => m.id === contrato.modalidade?.id) || null,
    });
  }

  fecharTrocarModalidade = () => {
    this.setState({ trocarModalidadeVisivel: false, trocarModalidadeSelecionada: null });
  }

  confirmarTrocarModalidade = async () => {
    const contrato = this.gridRef?.getSelected()?.[0];
    const novaModalidade = this.state.trocarModalidadeSelecionada;
    if (!contrato || !novaModalidade) return;

    this.setState({ trocarModalidadeSalvando: true });
    const resultado = await Contrato.trocarModalidade(contrato.id, novaModalidade.id);
    this.setState({ trocarModalidadeSalvando: false });

    if (!resultado.status) {
      const detalhe = UtilService.traduzir(resultado?.data?.state) || resultado?.data?.mensagem || '';
      UtilService.showToast({ sticky: true, severity: 'error', summary: UtilService.traduzir('main.contrato.trocarmodalidade.erro'), detail: detalhe });
    } else {
      UtilService.showToast({ severity: 'success', summary: UtilService.traduzir('main.contrato.trocarmodalidade.sucesso') });
      this.fecharTrocarModalidade();
      await this.gridRef?.atualizar();
    }
  }

  trocarModalidadeDialog() {
    const { trocarModalidadeVisivel, trocarModalidadeOpcoes, trocarModalidadeSelecionada, trocarModalidadeSalvando } = this.state;
    return (
      <Dialog
        header={UtilService.traduzir('main.contrato.trocarmodalidade')}
        visible={trocarModalidadeVisivel}
        className="w-4"
        draggable={false}
        onHide={this.fecharTrocarModalidade}
      >
        <p>{UtilService.traduzir('main.contrato.trocarmodalidade.confirmacao.mensagem')}</p>
        <Dropdown
          className="w-full mt-2"
          value={trocarModalidadeSelecionada}
          options={trocarModalidadeOpcoes}
          optionLabel="nome"
          placeholder={UtilService.traduzir('main.contrato.modalidade')}
          onChange={(e) => this.setState({ trocarModalidadeSelecionada: e.value })}
        />
        <div className="flex justify-content-end gap-2 mt-4">
          <Button
            type="button"
            label={UtilService.traduzir('main.cancelar')}
            className="p-button-text"
            onClick={this.fecharTrocarModalidade}
            disabled={trocarModalidadeSalvando}
          />
          <Button
            type="button"
            label={UtilService.traduzir('main.contrato.trocarmodalidade.confirmar')}
            loading={trocarModalidadeSalvando}
            disabled={!trocarModalidadeSelecionada}
            onClick={this.confirmarTrocarModalidade}
          />
        </div>
      </Dialog>
    );
  }

  provisionar = () => {
    const contrato = this.gridRef?.getSelected()?.[0];
    if (!contrato) return;

    confirmDialog({
      message: <p>{UtilService.traduzir('main.contrato.provisionar.confirmacao.mensagem')}</p>,
      className: 'w-3',
      header: UtilService.traduzir('main.contrato.provisionar.confirmacao.header'),
      icon: 'pi pi-exclamation-triangle p-error',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      draggable: false,
      accept: async () => {
        const resultado = await Contrato.provisionar(contrato.id);
        if (!resultado.status) {
          const detalhe = UtilService.traduzir(resultado?.data?.state) || resultado?.data?.mensagem || '';
          UtilService.showToast({ sticky: true, severity: 'error', summary: UtilService.traduzir('main.contrato.provisionar.erro'), detail: detalhe });
        } else {
          UtilService.showToast({ severity: 'success', summary: UtilService.traduzir('main.contrato.provisionar.sucesso') });
        }
      },
    });
  }

  processos() {
    const { permissoes } = this.props;
    const podeProvisionar = permissoes?.contrato?.processos?.some(
      e => e.state === 'main.contrato.provisionar' && e.permitido
    );
    const podeTrocarModalidade = permissoes?.contrato?.processos?.some(
      e => e.state === 'main.contrato.trocarmodalidade' && e.permitido
    );

    const lista = [];

    if (podeProvisionar) {
      lista.push({
        label: 'main.contrato.provisionar',
        icon: 'pi pi-send',
        disabled: true,
        ativo: (processo, selected) => {
          processo.disabled = selected.length !== 1 || selected[0]?.situacao !== 'main.contrato.situacao.pendente';
          return processo;
        },
        command: () => this.provisionar(),
      });
    }

    if (podeTrocarModalidade) {
      lista.push({
        label: 'main.contrato.trocarmodalidade',
        icon: 'pi pi-sync',
        disabled: true,
        ativo: (processo, selected) => {
          processo.disabled = selected.length !== 1 || selected[0]?.situacao !== 'main.contrato.situacao.ativo';
          return processo;
        },
        command: () => this.abrirTrocarModalidade(),
      });
    }

    return lista;
  }

  render() {
    return (
      <>
      <EntityGrid
        ref={(ref) => this.gridRef = ref}
        entidade={Contrato}
        model={model}
        identidade="main.contrato"
        permissoes={this.props.permissoes}
        icone="pi-building"
        storageKey="dt-contratos"
        processos={this.processos()}
        renderers={renderers}
        configuracao={this.props.configuracao}
        navigate={this.props.navigate}
        location={this.props.location}
        searchparams={this.props.searchparams}
        formulario={(m, ctx) => <ContratoFormulario model={m} contexto={ctx} />}
      />
      {this.trocarModalidadeDialog()}
      </>
    );
  }
}

export default ContratoTemplate;
